import _ from 'lodash';
import helpers from 'app/app-helpers';

const FAQ_CONTEXT = 'faq';
const defaultPaging = {
  pageNumber: 0,
  pageSize: 1000,
};

function initializeModel(deps, model) {
  return _.merge(model, {
    faq: {
      categories: [],
      visibilities: [],
      isMaintenance: false,
      faq: {
        loaded: false,
      },
      faqCategory: {
        languageValidation: {},
      },
      faqEntry: {
        languageValidation: {},
      },
    },
  });
}

function loadDictionaries(deps, model) {
  return loadVisibilityOptionNames(deps, model);
}

function loadVisibilityOptionNames(deps, model) {
  return deps.restSrv
    .listCached(FAQ_CONTEXT, model.session.rest.faq.dictionary.visibility.url, { locale: model.session.locale })
    .then((response) => {
      model.faq.visibilities = Object.keys(response.values).map((key) => ({ id: key, label: response.values[key] }));
      return model;
    });
}

function handleRestResponse(deps, model, response = {}) {
  if (!response.code) {
    return loadData(deps, model);
  }
  return model;
}

function loadData(deps, model, editMode = true) {
  return Promise.all([loadSortedCategories(deps, model, editMode), loadEntries(deps, model, editMode)]).then((response) => {
    model.faq.categories = response[0];
    model.faq.entries = response[1];
    const fullList = getCombinedEntriesWithCategories(model.faq.categories, model.faq.entries);
    model.faq.list = editMode ? fullList : getOnlyPopulatedCategories(fullList);
    model.faq.cachedList = _.cloneDeep(model.faq.list);
    model.faq.loaded = true;
    return model;
  });
}

function loadSortedCategories(deps, model, editMode) {
  Object.assign(defaultPaging, { locale: '*' });
  const resource = editMode ? model.session.rest.faq.admin.categories.url : model.session.rest.faq.user.categories.url;
  return deps.restSrv
    .list(FAQ_CONTEXT, resource, defaultPaging)
    .then((response) => response.content.sort(helpers.compare.bind(null, 'ordinalNo')))
    .catch((err) => {
      model = deps.checkAndSetMaintenance(err, model, 'faq');
    });
}

function loadEntries(deps, model, editMode) {
  Object.assign(defaultPaging, { locale: '*' });
  const resource = editMode ? model.session.rest.faq.admin.entries.url : model.session.rest.faq.user.entries.url;
  return deps.restSrv
    .list(FAQ_CONTEXT, resource, defaultPaging)
    .then((response) => response.content)
    .catch((err) => {
      model = deps.checkAndSetMaintenance(err, model, 'faq');
      return model;
    });
}

function search(deps, model, phrase) {
  model.faq.list = phrase === '' ? model.faq.cachedList : filterList(model, model.faq.cachedList, phrase);
  return model;
}

function getOnlyPopulatedCategories(_list) {
  const list = _.cloneDeep(_list);
  return list.filter((category) => category.entries.length);
}

function getCombinedEntriesWithCategories(_categories, _entries) {
  const categories = _.cloneDeep(_categories);
  const entries = getEntriesGroupedById(_.cloneDeep(_entries));
  return combineEntriesWithCategories(categories, entries);
}

function combineEntriesWithCategories(categories, entries) {
  return categories.reduce((result, category) => {
    const catId = category.id;
    category.entries = entries[catId] ? entries[catId].sort(helpers.compare.bind(null, 'ordinalNo')) : [];
    result.push(category);
    return result;
  }, []);
}

function getEntriesGroupedById(entries = []) {
  return entries.sort(helpers.compare.bind(null, 'categoryId')).reduce((result, entry) => {
    const catId = entry.categoryId;
    if (result[catId] === undefined) {
      result[catId] = [];
    }

    result[catId].push(entry);
    return result;
  }, {});
}

function filterList(model, _list, phrase) {
  const phraseLowerCase = phrase.toLowerCase();
  const list = _.cloneDeep(_list);
  return list.reduce((result, category) => {
    const categoryNameContainsPhrase =
      category.faqCategoryTranslationsDto[model.session.locale].name.toLowerCase().indexOf(phraseLowerCase) > -1;

    if (!categoryNameContainsPhrase) {
      category.entries = category.entries.filter(
        (entry) =>
          (entry.faqEntryTranslationsDto[model.session.locale].name + entry.faqEntryTranslationsDto[model.session.locale].value)
            .toLowerCase()
            .indexOf(phraseLowerCase) > -1
      );
    }

    if (category.entries.length || categoryNameContainsPhrase) {
      result.push(category);
    }
    return result;
  }, []);
}

// ////////////// ADMIN ///////////////////

function saveEntry(deps, model, entry) {
  const { id } = entry;
  const transformedEntry = transformEntryForSave(model, entry);
  const restPromise = id
    ? deps.restSrv.update(FAQ_CONTEXT, model.session.rest.faq.entry.url, transformedEntry, { id })
    : deps.restSrv.create(FAQ_CONTEXT, model.session.rest.faq.admin.entries.url, transformedEntry, {});
  return restPromise.then(handleRestResponse.bind(null, deps, model));
}

function deleteEntry(deps, model, entry) {
  return deps.restSrv
    .remove(
      FAQ_CONTEXT,
      model.session.rest.faq.entry.url,
      { id: entry.id },
      { data: { name: entry.faqEntryTranslationsDto[model.session.locale].name } }
    )
    .then(() => loadData(deps, model));
}

function transformEntryForSave(model, entry) {
  Object.keys(entry.faqEntryTranslationsDto).map((locale) => {
    entry.faqEntryTranslationsDto[locale].locale = locale;
  });
  return {
    categoryId: entry.categoryId,
    id: entry.id,
    faqEntryTranslationsDto: entry.faqEntryTranslationsDto,
    visibility: [entry.visibility[0]],
    ordinalNo: getProperOrdinalNo(model.faq.list, entry),
    modifyDate: new Date().getTime(),
  };
}

function getProperOrdinalNo(list, entry) {
  if (entry.id) {
    return entry.ordinalNo;
  }

  const catId = entry.categoryId;
  const categoryEntries = getCategoryEntries(list, catId);
  return !categoryEntries.length ? 1 : categoryEntries[categoryEntries.length - 1].ordinalNo + 1;
}

function getCategoryEntries(list, id) {
  const filteredCategory = list.filter((category) => category.id === id);
  return filteredCategory.length === 1 ? filteredCategory[0].entries : [];
}

function updateCategory(deps, model, category) {
  const transformedCategory = transformCategoryForUpdate(category);
  return deps.restSrv
    .update(FAQ_CONTEXT, model.session.rest.faq.category.url, transformedCategory, { id: category.id })
    .then(handleRestResponse.bind(null, deps, model));
}

function saveCategory(deps, model, category) {
  const transformedCategory = transformCategoryForCreate(deps, model, category);
  return deps.restSrv
    .create(FAQ_CONTEXT, model.session.rest.faq.admin.categories.url, transformedCategory, {})
    .then(handleRestResponse.bind(null, deps, model));
}

function transformCategoryForCreate(deps, model, category) {
  const { categories } = model.faq;
  const lastIndex = !categories.length ? 0 : categories.length - 1;
  const lastCategory = model.faq.categories[lastIndex];
  const nextOrdinalNo = lastCategory ? lastCategory.ordinalNo + 1 : 1;
  Object.keys(category.faqCategoryTranslationsDto).map((locale) => {
    category.faqCategoryTranslationsDto[locale].locale = locale;
  });
  return {
    faqCategoryTranslationsDto: category.faqCategoryTranslationsDto,
    ordinalNo: nextOrdinalNo,
  };
}

function transformCategoryForUpdate(category) {
  Object.keys(category.faqCategoryTranslationsDto).map((locale) => {
    category.faqCategoryTranslationsDto[locale].locale = locale;
  });
  return {
    id: category.id,
    faqCategoryTranslationsDto: category.faqCategoryTranslationsDto,
    ordinalNo: category.ordinalNo,
    modifyDate: new Date().getTime(),
  };
}

function deleteCategory(deps, model, category) {
  return deps.restSrv
    .remove(
      FAQ_CONTEXT,
      model.session.rest.faq.category.url,
      { id: category.id },
      { data: { name: _.get(category, `faqCategoryTranslationsDto[${model.session.locale}].name`, 'undefined') } }
    )
    .then(handleRestResponse.bind(null, deps, model));
}

// drag n drop
function accept(deps, sourceNodeScope, destNodesScope) {
  const sourceHasCategory = !!sourceNodeScope.$modelValue.categoryId;
  return destNodesScope.$modelValue.every((destNodeModelValue) => {
    const destHasCategory = !!destNodeModelValue.categoryId;
    return destHasCategory === sourceHasCategory;
  });
}

function dropped(deps, model, event) {
  const sourceNodeScope = event.source.nodeScope;
  const destNodesScope = event.dest.nodesScope;
  const isCategory = !sourceNodeScope.$modelValue.categoryId;
  let restPromise = Promise.resolve();

  if (isCategory) {
    const destCategories = transformCategoriesForOrdinal(destNodesScope.$modelValue);
    restPromise = reorderCategories(deps, model, destCategories);
  } else {
    const sourceCategoryId = sourceNodeScope.$modelValue.categoryId;
    const destCategoryId = destNodesScope.$parent.$modelValue.id;

    if (sourceCategoryId !== destCategoryId) {
      const destEntry = transformEntryForOrderialUpdate(sourceNodeScope.entry);
      destEntry.categoryId = destCategoryId;
      restPromise = deps.restSrv.update(FAQ_CONTEXT, model.session.rest.faq.entry.url, destEntry, { id: destEntry.id });
    }

    const destEntries = transformEntriesForOrdinal(destNodesScope.$modelValue);
    restPromise.then(() => reorderEntries(deps, model, destEntries, destCategoryId));
  }

  return restPromise.then(() => {
    deps.Notifications.alertSuccess('Order has changed successfully');
    return model;
  });
}

function reorderEntries(deps, model, entries) {
  return deps.restSrv.update(FAQ_CONTEXT, model.session.rest.faq.entryOrdinal.url, entries, true).then(() => {
    updateAfterOrderChange(model);
  });
}

function reorderCategories(deps, model, categories) {
  return deps.restSrv.update(FAQ_CONTEXT, model.session.rest.faq.categoryOrdinal.url, categories, true).then(() => {
    updateAfterOrderChange(model);
  });
}

function updateAfterOrderChange(model) {
  model.faq.list = model.faq.list.map((category, catIndex) => {
    category.ordinalNo = catIndex + 1;
    category.entries = category.entries.map((entry, index) => {
      entry.ordinalNo = index + 1;
      entry.categoryId = category.id;
      return entry;
    });
    return category;
  });
}

function transformEntriesForOrdinal(entries) {
  return entries.map((entry, index) => {
    const mapped = {
      entryId: entry.id,
      ordinalNo: index + 1,
    };
    return mapped;
  });
}

function transformCategoriesForOrdinal(categories) {
  return categories.map((category, index) => {
    const mapped = {
      categoryId: category.id,
      ordinalNo: index + 1,
    };
    return mapped;
  });
}

function transformEntryForOrderialUpdate(entry) {
  return {
    id: entry.id,
    categoryId: entry.categoryId,
    modifyDate: entry.modifyDate,
    name: entry.name,
    value: entry.value,
    visibility: entry.visibility,
  };
}

export default {
  initializeModel,
  loadDictionaries,
  loadData,
  search,
  saveCategory,
  saveEntry,
  updateCategory,
  deleteCategory,
  deleteEntry,
  accept,
  dropped,
};



// WEBPACK FOOTER //
// ./src/modules/faq/logic/faq.js